import React, { useState } from 'react';
import { PlusIcon, CheckIcon, XIcon, EditIcon, RefreshCwIcon, BellIcon, MessageSquareIcon, MailIcon, GlobeIcon, ToggleLeftIcon, ToggleRightIcon } from 'lucide-react';
import { NotificationChannel, TeamsConfig, SlackConfig, EmailConfig, WebhookConfig } from './NotificationsManagement';
import { StatusBadge } from '../../StatusBadge';
interface NotificationChannelsProps {
  channels: NotificationChannel[];
  setChannels: React.Dispatch<React.SetStateAction<NotificationChannel[]>>;
  locale: string;
}
export function NotificationChannels({
  channels,
  setChannels,
  locale
}: NotificationChannelsProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<NotificationChannel | null>(null);
  const [testResult, setTestResult] = useState<{
    channelId: string;
    success: boolean;
    message: string;
  } | null>(null);
  const getChannelIcon = (type: string) => {
    switch (type) {
      case 'teams':
        return <MessageSquareIcon className="h-5 w-5 text-blue-500" data-id="element-17234" />;
      case 'slack':
        return <MessageSquareIcon className="h-5 w-5 text-purple-500" data-id="element-17235" />;
      case 'email':
        return <MailIcon className="h-5 w-5 text-gray-500" data-id="element-17236" />;
      case 'webhook':
        return <GlobeIcon className="h-5 w-5 text-green-500" data-id="element-17237" />;
      default:
        return <BellIcon className="h-5 w-5 text-gray-500" data-id="element-17238" />;
    }
  };
  const handleToggleChannel = (channelId: string) => {
    setChannels(channels.map(channel => channel.id === channelId ? {
      ...channel,
      isEnabled: !channel.isEnabled
    } : channel));
  };
  const handleEditChannel = (channel: NotificationChannel) => {
    setEditingChannel(channel);
    setIsDrawerOpen(true);
  };
  const handleTestConnection = (channelId: string) => {
    // In a real app, this would make an API call to test the connection
    const channel = channels.find(c => c.id === channelId);
    if (!channel || !channel.isConfigured) {
      setTestResult({
        channelId,
        success: false,
        message: 'Channel is not properly configured.'
      });
      return;
    }
    // Simulate API call with a random success/failure
    setTimeout(() => {
      const success = Math.random() > 0.3; // 70% chance of success
      setTestResult({
        channelId,
        success,
        message: success ? 'Test message sent successfully!' : 'Failed to send test message. Please check your configuration.'
      });
    }, 1000);
  };
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingChannel(null);
  };
  const handleSaveChannel = (updatedChannel: NotificationChannel) => {
    setChannels(channels.map(channel => channel.id === updatedChannel.id ? updatedChannel : channel));
    setIsDrawerOpen(false);
    setEditingChannel(null);
  };
  return <div data-id="element-17239">
      <div className="mb-6 flex justify-between items-center" data-id="element-17240">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17241">
          Notification Channels
        </h3>
        <button type="button" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17242">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-17243" />
          Add Custom Channel
        </button>
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2" data-id="element-17244">
        {channels.map(channel => <div key={channel.id} className="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200" data-id="element-17245">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-17246">
              <div className="flex items-center" data-id="element-17247">
                {getChannelIcon(channel.type)}
                <h3 className="ml-2 text-lg leading-6 font-medium text-gray-900" data-id="element-17248">
                  {channel.name}
                </h3>
              </div>
              <div data-id="element-17249">
                <button type="button" onClick={() => handleToggleChannel(channel.id)} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17250">
                  {channel.isEnabled ? <ToggleRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-17251" /> : <ToggleLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-17252" />}
                </button>
              </div>
            </div>
            <div className="px-4 py-5 sm:p-6" data-id="element-17253">
              <div className="flex justify-between items-center mb-4" data-id="element-17254">
                <div className="flex space-x-2" data-id="element-17255">
                  <StatusBadge status={channel.isEnabled ? 'success' : 'error'} text={channel.isEnabled ? 'Enabled' : 'Disabled'} data-id="element-17256" />
                  <StatusBadge status={channel.isConfigured ? 'success' : 'warning'} text={channel.isConfigured ? 'Configured' : 'Not Configured'} data-id="element-17257" />
                </div>
              </div>
              {channel.type === 'teams' && <div className="mt-2 text-sm text-gray-500" data-id="element-17258">
                  <p data-id="element-17259">
                    Microsoft Teams integration for sending notifications
                    directly to users or channels.
                  </p>
                  {channel.isConfigured && <div className="mt-2" data-id="element-17260">
                      <p data-id="element-17261">
                        Delivery Method:{' '}
                        {(channel.configuration as TeamsConfig).deliveryMethod === 'direct' ? 'Direct Message' : 'Channel Post'}
                      </p>
                      {(channel.configuration as TeamsConfig).defaultChannel && <p data-id="element-17262">
                          Default Channel:{' '}
                          {(channel.configuration as TeamsConfig).defaultChannel}
                        </p>}
                    </div>}
                </div>}
              {channel.type === 'slack' && <div className="mt-2 text-sm text-gray-500" data-id="element-17263">
                  <p data-id="element-17264">
                    Slack integration for sending notifications to workspace
                    channels.
                  </p>
                  {channel.isConfigured && <div className="mt-2" data-id="element-17265">
                      <p data-id="element-17266">
                        Default Channel:{' '}
                        {(channel.configuration as SlackConfig).defaultChannel}
                      </p>
                    </div>}
                </div>}
              {channel.type === 'email' && <div className="mt-2 text-sm text-gray-500" data-id="element-17267">
                  <p data-id="element-17268">Email notifications sent to user email addresses.</p>
                  {channel.isConfigured && <div className="mt-2" data-id="element-17269">
                      <p data-id="element-17270">
                        From:{' '}
                        {(channel.configuration as EmailConfig).fromAddress}
                      </p>
                      <p data-id="element-17271">
                        Connection:{' '}
                        {(channel.configuration as EmailConfig).connectionType}
                      </p>
                    </div>}
                </div>}
              {channel.type === 'webhook' && <div className="mt-2 text-sm text-gray-500" data-id="element-17272">
                  <p data-id="element-17273">
                    Custom webhook integration for sending notifications to
                    external systems.
                  </p>
                  {channel.isConfigured && <div className="mt-2" data-id="element-17274">
                      <p data-id="element-17275">
                        URL:{' '}
                        {(channel.configuration as WebhookConfig).url.substring(0, 30)}
                        ...
                      </p>
                      <p data-id="element-17276">
                        Auth Type:{' '}
                        {(channel.configuration as WebhookConfig).authType}
                      </p>
                    </div>}
                </div>}
              {testResult && testResult.channelId === channel.id && <div className={`mt-3 p-2 text-sm rounded-md ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-id="element-17277">
                  {testResult.message}
                </div>}
              <div className="mt-4 flex space-x-3" data-id="element-17278">
                <button type="button" onClick={() => handleEditChannel(channel)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17279">
                  <EditIcon className="h-4 w-4 mr-2" data-id="element-17280" />
                  Configure
                </button>
                <button type="button" onClick={() => handleTestConnection(channel.id)} disabled={!channel.isConfigured} className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${channel.isConfigured ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-indigo-500' : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'}`} data-id="element-17281">
                  <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-17282" />
                  Test Connection
                </button>
              </div>
            </div>
          </div>)}
      </div>
      {/* Configuration Drawer - would be implemented with proper form fields for each channel type */}
      {isDrawerOpen && editingChannel && <div className="fixed inset-0 overflow-hidden z-50" data-id="element-17283">
          <div className="absolute inset-0 overflow-hidden" data-id="element-17284">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDrawer} data-id="element-17285"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-17286">
              <div className="relative w-screen max-w-md" data-id="element-17287">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll" data-id="element-17288">
                  <div className="px-4 py-6 sm:px-6" data-id="element-17289">
                    <div className="flex items-start justify-between" data-id="element-17290">
                      <h2 className="text-lg font-medium text-gray-900" data-id="element-17291">
                        Configure {editingChannel.name}
                      </h2>
                      <div className="ml-3 h-7 flex items-center" data-id="element-17292">
                        <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-17293">
                          <span className="sr-only" data-id="element-17294">Close panel</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-17295" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 py-6 overflow-y-auto px-4 sm:px-6" data-id="element-17296">
                    {/* Form fields would go here based on channel type */}
                    <p className="text-sm text-gray-500 mb-4" data-id="element-17297">
                      Configure your {editingChannel.name} integration settings.
                    </p>
                    {/* This is a placeholder for the actual configuration form */}
                    <div className="space-y-6" data-id="element-17298">
                      <p className="text-sm text-gray-500" data-id="element-17299">
                        In a real implementation, this would contain the
                        appropriate form fields for configuring{' '}
                        {editingChannel.type} integration.
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 px-4 py-4 flex justify-end border-t border-gray-200" data-id="element-17300">
                    <button type="button" className="mr-3 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-17301">
                      Cancel
                    </button>
                    <button type="button" className="bg-indigo-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={() => {
                  // In a real app, we would validate and save the form data
                  const updatedChannel = {
                    ...editingChannel,
                    isConfigured: true
                  };
                  handleSaveChannel(updatedChannel);
                }} data-id="element-17302">
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}